import React from "react";
import pnoeLogo from "../../common/images/logo.png";
import useTheme from "../../common/hooks/useTheme";
import AiSummaryRecommendationsView from "./AiSummaryRecommendationsView";
import {
  AiProgressMeasurement,
  AiProgressMetric,
  AiProgressSection,
  AiProgressSummary,
  AiProgressReportContent,
} from "./aiProgressSummaryTypes";
import { AiSummaryTakeawayItem } from "./aiSummaryTypes";
import classes from "./AiProgressSummaryView.module.css";

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 12 }).format(value);

export const getProgressReferenceRange = (metric: AiProgressMetric) => {
  const min = metric.referenceMin;
  const max = metric.referenceMax;
  if (min === undefined && max === undefined) return undefined;
  if (min !== undefined && max !== undefined && min > max) return undefined;
  return { min, max };
};

export const getProgressChartDomain = (metric: AiProgressMetric) => {
  const values = metric.measurements.map((point) => point.value);
  const reference = getProgressReferenceRange(metric);
  if (reference) {
    const limits = [reference.min, reference.max].filter(
      (n): n is number => n !== undefined
    );
    const low = Math.min(...values, ...limits);
    const high = Math.max(...values, ...limits);
    const padding = (high - low || Math.abs(high) || 1) * 0.22;
    return { min: low - padding, max: high + padding };
  }
  return {
    min: Math.min(0, ...values),
    max: Math.max(metric.unit === "%" ? 100 : 0, ...values),
  };
};

const ReferenceLabel = ({ metric }: { metric: AiProgressMetric }) => {
  const reference = getProgressReferenceRange(metric);
  if (!reference) return null;
  const { min, max } = reference;
  const label =
    min === undefined
      ? `up to ${formatNumber(max!)}`
      : max === undefined
      ? `at least ${formatNumber(min)}`
      : `${formatNumber(min)} - ${formatNumber(max)}`;
  return (
    <p className={classes.referenceLabel}>
      Reference: {label}
      {metric.unit && ` ${metric.unit}`}
    </p>
  );
};

export const formatProgressDate = (value: string): string => {
  if (!value) return "";
  const short = value.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (!short && !iso) return value;
  const [year, month, day] = short
    ? [+short[3], +short[1], +short[2]]
    : [+iso![1], +iso![2], +iso![3]];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const dateLabel = (point: AiProgressMeasurement, index: number) =>
  formatProgressDate(point.date) || `Test ${index + 1}`;
const valueLabel = (point: AiProgressMeasurement, unit: string) =>
  `${formatNumber(point.value)}${unit === "%" ? "" : unit ? " " : ""}${unit}`;

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className={classes.sectionTitle}>{children}</h2>
);

const Narrative = ({ title, children }: { title: string; children: string }) =>
  children ? (
    <div className={classes.narrative} data-pdf-break="after">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  ) : null;

const TakeawayItem = ({
  item,
  positive,
}: {
  item: AiSummaryTakeawayItem;
  positive: boolean;
}) => (
  <article
    className={`${classes.takeawayCard} ${
      positive ? classes.positive : classes.attention
    }`}
  >
    {item.title && <h4>{item.title}</h4>}
    {item.text && <p>{item.text}</p>}
    {!!item.points?.length && (
      <ul>
        {item.points.map((point, i) => (
          <li key={i}>{point}</li>
        ))}
      </ul>
    )}
  </article>
);

const Takeaways = ({
  summary,
}: {
  summary: AiProgressSummary["keyTakeaway"];
}) => {
  const count = Math.max(summary.improved.length, summary.attention.length);
  if (!summary.overallPicture && !count) return null;
  const single = !summary.improved.length || !summary.attention.length;
  return (
    <section className={classes.section}>
      <SectionTitle>Key takeaway</SectionTitle>
      <Narrative title="Overall picture">{summary.overallPicture}</Narrative>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className={`${classes.takeawayRow} ${
            single ? classes.singleColumn : ""
          }`}
          data-pdf-break="after"
        >
          {summary.improved[index] && (
            <div>
              {index === 0 && (
                <h3 className={classes.positiveHeading}>What improved</h3>
              )}
              <TakeawayItem item={summary.improved[index]} positive />
            </div>
          )}
          {summary.attention[index] && (
            <div className={!single ? classes.attentionColumn : undefined}>
              {index === 0 && (
                <h3 className={classes.attentionHeading}>
                  What needs attention
                </h3>
              )}
              <TakeawayItem item={summary.attention[index]} positive={false} />
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

const MeasurementValues = ({
  points,
  unit,
}: {
  points: AiProgressMeasurement[];
  unit: string;
}) => (
  <div className={classes.comparisonValues}>
    {points.map((point, index) => (
      <div key={index}>
        <span>{dateLabel(point, index)}</span>
        <strong>
          {formatNumber(point.value)}
          {unit && <small>{unit}</small>}
        </strong>
      </div>
    ))}
  </div>
);

const TrendChart = ({ metric }: { metric: AiProgressMetric }) => {
  const theme = useTheme();
  const points = metric.measurements;
  if (points.length < 2)
    return <MeasurementValues points={points} unit={metric.unit} />;
  // Dense histories remain readable on A4 instead of shrinking or dropping tests.
  if (points.length > 6)
    return (
      <table className={classes.historyTable}>
        <thead>
          <tr>
            <th>Test date</th>
            <th>Value {metric.unit && `(${metric.unit})`}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point, i) => (
            <tr key={i}>
              <td>{dateLabel(point, i)}</td>
              <td>{formatNumber(point.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  const { min, max } = getProgressChartDomain(metric);
  const reference = getProgressReferenceRange(metric);
  const range = max - min || 1;
  const width = 300;
  const x = (i: number) => 42 + (i * (width - 70)) / (points.length - 1);
  const y = (value: number) => 125 - ((value - min) / range) * 92;
  const muted = theme.colors_ds.NEUTRAL_TEXT_WEAK;
  const line = theme.colorTokens.S[70];
  return (
    <>
      <svg
        className={classes.chart}
        viewBox={`0 0 ${width} 145`}
        fontFamily="sans-serif"
        role="img"
        aria-label={`${metric.label}: ${points
          .map((p, i) => `${dateLabel(p, i)}: ${valueLabel(p, metric.unit)}`)
          .join("; ")}`}
      >
        {reference && (
          <g data-reference-band="true">
            <rect
              x="38"
              y={y(reference.max ?? max)}
              width={width - 56}
              height={Math.max(
                0,
                y(reference.min ?? min) - y(reference.max ?? max)
              )}
              fill={line}
              fillOpacity="0.09"
            />
            {[reference.min, reference.max]
              .filter((value): value is number => value !== undefined)
              .map((value, i) => (
                <line
                  key={i}
                  x1="38"
                  x2={width - 18}
                  y1={y(value)}
                  y2={y(value)}
                  stroke={line}
                  strokeOpacity="0.45"
                  strokeDasharray="3 3"
                />
              ))}
          </g>
        )}
        {[min, min + range / 2, min + range].map((value, i) => (
          <g key={i}>
            <line
              x1="38"
              x2={width - 18}
              y1={y(value)}
              y2={y(value)}
              stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
              strokeDasharray="3 4"
            />
            <text
              x="30"
              y={y(value) + 4}
              textAnchor="end"
              fill={muted}
              fontSize="12"
            >
              {new Intl.NumberFormat("en-US", {
                maximumSignificantDigits: 3,
              }).format(value)}
            </text>
          </g>
        ))}
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(" ")}
          fill="none"
          stroke={line}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {points.map((point, i) => (
          <circle key={i} cx={x(i)} cy={y(point.value)} r="4" fill={line} />
        ))}
      </svg>
      <div
        className={classes.metricMeasurements}
        style={{
          gridTemplateColumns: `repeat(${Math.min(
            points.length,
            3
          )}, minmax(0, 1fr))`,
        }}
      >
        {points.map((point, i) => (
          <div key={i}>
            <strong>{valueLabel(point, metric.unit)}</strong>
            <span>{dateLabel(point, i)}</span>
          </div>
        ))}
      </div>
    </>
  );
};

const AgeComparison = ({
  age,
}: {
  age: AiProgressSummary["biologicalAge"];
}) => {
  const theme = useTheme();
  if (!age.measurements.length && age.chronologicalAge === undefined)
    return null;
  const rings = [
    ...age.measurements.map((point, i) => ({
      value: point.value,
      label: dateLabel(point, i),
    })),
    ...(age.chronologicalAge === undefined
      ? []
      : [{ value: age.chronologicalAge, label: "Chronological age" }]),
  ];
  const maxAge = Math.max(100, ...rings.map((r) => r.value));
  return (
    <section className={classes.section}>
      <SectionTitle>Biological age</SectionTitle>
      <div
        className={`${classes.ageCard} ${
          rings.length > 5 ? classes.ageValuesOnly : ""
        }`}
        data-pdf-break="after"
      >
        {rings.length <= 5 && (
          <svg
            viewBox="0 0 320 195"
            fontFamily="sans-serif"
            className={classes.ageGauge}
            role="img"
            aria-label={`Age comparison: ${rings
              .map((r) => `${r.label}: ${formatNumber(r.value)} years`)
              .join("; ")}`}
          >
            {rings.map((ring, i) => {
              const radius = 132 - i * 23;
              const progress = Math.max(0, Math.min(1, ring.value / maxAge));
              const angle = Math.PI * (1 - progress);
              const x = 160 + Math.cos(angle) * radius;
              const y = 165 - Math.sin(angle) * radius;
              const color =
                i === rings.length - 1 && age.chronologicalAge !== undefined
                  ? theme.colorTokens.N[70]
                  : theme.colorTokens.S[Math.min(90, 50 + i * 10)];
              const path = `M ${160 - radius} 165 A ${radius} ${radius} 0 0 1 ${
                160 + radius
              } 165`;
              return (
                <g key={i}>
                  <path
                    d={path}
                    fill="none"
                    stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK}
                    strokeWidth="8"
                  />
                  <path
                    d={path}
                    fill="none"
                    stroke={color}
                    strokeWidth="8"
                    pathLength="100"
                    strokeDasharray={`${progress * 100} 100`}
                    strokeLinecap="round"
                  />
                  <circle
                    cx={x}
                    cy={y}
                    r="10"
                    fill={theme.colors_ds.NEUTRAL_BG_MAIN}
                    stroke={color}
                    strokeWidth="2"
                  />
                  <text
                    x={x}
                    y={y + 3.5}
                    textAnchor="middle"
                    fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
                    fontSize="9"
                    fontWeight="700"
                  >
                    {formatNumber(ring.value)}
                  </text>
                </g>
              );
            })}
            <text
              x="28"
              y="187"
              fill={theme.colors_ds.NEUTRAL_TEXT_WEAK}
              fontSize="10"
            >
              0
            </text>
            <text
              x="292"
              y="187"
              textAnchor="end"
              fill={theme.colors_ds.NEUTRAL_TEXT_WEAK}
              fontSize="10"
            >
              {formatNumber(maxAge)}
            </text>
          </svg>
        )}
        <div className={classes.ageValues}>
          {!!age.measurements.length && (
            <MeasurementValues points={age.measurements} unit="years" />
          )}
          {age.chronologicalAge !== undefined && (
            <div className={classes.chronological}>
              <span>Chronological age</span>
              <strong>
                {formatNumber(age.chronologicalAge)} <small>years</small>
              </strong>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const MetricCard = ({ metric }: { metric: AiProgressMetric }) => (
  <article className={classes.metricCard}>
    <h3>{metric.label}</h3>
    {metric.interpretation && (
      <p className={classes.interpretation}>{metric.interpretation}</p>
    )}
    <ReferenceLabel metric={metric} />
    <TrendChart metric={metric} />
  </article>
);

export const ComparisonSection = ({
  title,
  summaryTitle,
  section,
}: {
  title: string;
  summaryTitle: string;
  section: AiProgressSection;
}) => {
  if (!section.description && !section.headline && !section.metrics.length)
    return null;
  const rows = Array.from(
    { length: Math.ceil(section.metrics.length / 3) },
    (_, i) => section.metrics.slice(i * 3, i * 3 + 3)
  );
  return (
    <section className={classes.section} data-pdf-break-before="true">
      <SectionTitle>{title}</SectionTitle>
      <Narrative title={summaryTitle}>{section.description}</Narrative>
      {section.headline && (
        <div className={classes.headlineCard} data-pdf-break="after">
          <h3>{section.headline.label}</h3>
          <MeasurementValues
            points={section.headline.measurements}
            unit={section.headline.unit}
          />
          {section.headline.interpretation && (
            <p className={classes.interpretation}>
              {section.headline.interpretation}
            </p>
          )}
        </div>
      )}
      {rows.map((row, i) => (
        <div className={classes.metricRow} key={i} data-pdf-break="after">
          {row.map((metric, j) => (
            <MetricCard key={`${metric.id}-${j}`} metric={metric} />
          ))}
        </div>
      ))}
    </section>
  );
};

export const AiProgressReportFrame = ({
  summary,
  variant = "screen",
  children,
}: {
  summary: AiProgressReportContent;
  variant?: "screen" | "pdf";
  children: React.ReactNode;
}) => (
  <div
    className={`${classes.report} ${
      variant === "pdf" ? classes.pdf : classes.screen
    }`}
  >
    <header className={classes.hero} data-pdf-break="after">
      <div className={classes.brand}>
        <img src={pnoeLogo} alt="PNOE" />
        <span>Clinical Intelligence</span>
      </div>
      <div className={classes.identity}>
        <h1>Progress Report</h1>
        {summary.patient.name && <h2>{summary.patient.name}</h2>}
        {summary.patient.companyName && <p>{summary.patient.companyName}</p>}
        {!!summary.patient.testDates.length && (
          <p className={classes.dates}>
            Test dates:{" "}
            {summary.patient.testDates.map(formatProgressDate).join(" / ")}
          </p>
        )}
        {(summary.heroPill || summary.reportTypes.length > 0) && (
          <span className={classes.pill}>
            {summary.heroPill || summary.reportTypes.join(" + ")}
          </span>
        )}
        {summary.heroPill && summary.reportTypes.length > 0 && (
          <small className={classes.reportTypes}>
            {summary.reportTypes.join(" / ")}
          </small>
        )}
      </div>
    </header>
    <Takeaways summary={summary.keyTakeaway} />
    {children}
    <AiSummaryRecommendationsView
      trainingPlan={summary.trainingPlan}
      nutrition={summary.nutrition}
      recommendations={summary.recommendations}
      variant={variant}
    />
    {summary.disclaimer && (
      <section className={classes.disclaimer} data-pdf-break="after">
        <h2>Disclaimer</h2>
        <p>{summary.disclaimer}</p>
      </section>
    )}
  </div>
);

const AiProgressSummaryView = ({
  summary,
  variant = "screen",
}: {
  summary: AiProgressSummary;
  variant?: "screen" | "pdf";
}) => (
  <AiProgressReportFrame summary={summary} variant={variant}>
    <AgeComparison age={summary.biologicalAge} />
    <ComparisonSection
      title="RMR - Resting test"
      summaryTitle="Resting summary"
      section={summary.rmr}
    />
    <ComparisonSection
      title="AMR - Exercise test"
      summaryTitle="Exercise summary"
      section={summary.amr}
    />
    <ComparisonSection
      title="InBody - Body composition"
      summaryTitle="InBody summary"
      section={summary.inbody}
    />
  </AiProgressReportFrame>
);

export default AiProgressSummaryView;
